import React from 'react';
import { Helmet } from 'react-helmet-async';

// Schema markup for the Organization
const OrganizationSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'xdimension AI',
    url: 'https://xdimension.ai',
    logo: 'https://xdimension.ai/logo.png',
    sameAs: [
      'https://twitter.com/xdimensionAI',
      'https://linkedin.com/company/xdimension-ai',
      'https://instagram.com/xdimensionai'
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-800-XDIMENSION',
      contactType: 'customer service',
      areaServed: 'US',
      availableLanguage: 'English'
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: '123 Tech Avenue',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94105',
      addressCountry: 'US'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Schema markup for a Product
const ProductSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Ainous AI Assistant',
    image: 'https://xdimension.ai/ainous-product.jpg',
    description: 'Ainous is an advanced AI assistant in elegant robot hardware, designed to enhance your daily experiences and productivity.',
    brand: {
      '@type': 'Brand',
      name: 'xdimension'
    },
    offers: [
      {
        '@type': 'Offer',
        name: 'Essential',
        price: '798',
        priceCurrency: 'USD',
        availability: 'https://schema.org/PreOrder'
      },
      {
        '@type': 'Offer',
        name: 'Premium',
        price: '1398',
        priceCurrency: 'USD',
        availability: 'https://schema.org/PreOrder'
      },
      {
        '@type': 'Offer',
        name: 'Family',
        price: '1998',
        priceCurrency: 'USD',
        availability: 'https://schema.org/PreOrder'
      }
    ]
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Schema markup for a Blog Post
const BlogPostSchema = ({ article }) => {
  if (!article) return null;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    image: article.image,
    datePublished: article.publishedDate,
    dateModified: article.modifiedDate || article.publishedDate,
    author: {
      '@type': 'Person',
      name: typeof article.author === 'string' ? article.author : article.author.name || 'xdimension Team'
    },
    publisher: {
      '@type': 'Organization',
      name: 'xdimension AI',
      logo: {
        '@type': 'ImageObject',
        url: 'https://xdimension.ai/logo.png'
      }
    },
    description: article.description,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': article.url
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Schema markup for a FAQ
const FAQSchema = ({ faqs }) => {
  if (!faqs || faqs.length === 0) return null;
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

// Website Schema
const WebsiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'xdimension AI',
    url: 'https://xdimension.ai',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://xdimension.ai/search?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export { OrganizationSchema, ProductSchema, BlogPostSchema, FAQSchema, WebsiteSchema };
